import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const UPDATE_SAVED_SEARCHES_COUNT =
  'app/user/UPDATE_SAVED_SEARCHES_COUNT';

export const DISMISS_SYSTEM_NOTIFICATION =
  'app/user/DISMISS_SYSTEM_NOTIFICATION';

export const REMOVE_USER_NOTIFICATION =
  'app/user/REMOVE_USER_NOTIFICATION';

export const CLEAR_ALL_USER_NOTIFICATIONS =
  'app/user/CLEAR_ALL_USER_NOTIFICATIONS';

export const CLEAR_LISTING_PRICING_NOTIFICATIONS =
  'app/user/CLEAR_LISTING_PRICING_NOTIFICATIONS';

export const SEND_VERIFICATION_EMAIL_REQUEST =
  'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS =
  'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR =
  'app/user/SEND_VERIFICATION_EMAIL_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } =
    oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowTimestamp: 0,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserListingsCount: 0,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserSystemNotifications: [],
  currentUserFavoritesCount: 0,
  currentUserSavedSearchesCount: 0,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      const notifications =
        payload?.attributes?.profile?.privateData?.notifications || [];

      // Generate system notifications
      const generateSystemNotifications = user => {
        const systemNotifications = [];

        if (user?.attributes) {
          const { emailVerified } = user.attributes;
          const stripeConnected = !!user.stripeAccount?.id;

          // Generate notification for unverified email
          if (emailVerified === false) {
            systemNotifications.push({
              id: 'system-email-verification',
              type: 'error',
              message:
                'Please verify your email address to fully activate your account.',
              timestamp: new Date().toISOString(),
              read: false,
              link: '/account',
              dismissible: false, // Cannot be dismissed - user must take action
            });
          }

          // Generate notification for unconnected Stripe account
          // Note: We always generate this, but the selector will filter it based on hasListings
          if (stripeConnected === false) {
            systemNotifications.push({
              id: 'system-stripe-connection',
              type: 'error',
              message:
                'Connect your payout bank account to start selling on the platform.',
              timestamp: new Date().toISOString(),
              read: false,
              link: '/account/payments',
              requiresListings: true, // Flag to indicate this needs listings check
              dismissible: false, // Cannot be dismissed - user must take action
            });
          }
        }

        return systemNotifications;
      };

      const systemNotifications = generateSystemNotifications(payload);
      const allNotifications = [...systemNotifications, ...notifications];
      const unreadCount = allNotifications.filter(n => !n.read).length;

      const favorites =
        payload?.attributes?.profile?.privateData?.favorites || [];
      const favoritesCount = favorites.length;
      // Calculate saved searches count more robustly
      const savedSearches =
        payload?.attributes?.profile?.privateData?.savedSearches;
      const savedSearchesCount = Array.isArray(savedSearches)
        ? savedSearches.length
        : 0;

      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
        currentUserShowTimestamp: payload ? new Date().getTime() : 0,
        currentUserNotificationCount: unreadCount,
        currentUserFavoritesCount: favoritesCount,
        currentUserSavedSearchesCount: savedSearchesCount,
        currentUserSystemNotifications: systemNotifications,
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserListingsCount: 0,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        currentUserSystemNotifications: [],
        currentUserFavoritesCount: 0,
        currentUserSavedSearchesCount: 0,
      };

    case UPDATE_SAVED_SEARCHES_COUNT:
      return {
        ...state,
        currentUserSavedSearchesCount: payload.count,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserHasListings: payload.hasListings,
        currentUserListingsCount: payload.listingsCount,
      };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      // Don't overwrite notification count from private data
      // Keep the existing count which was calculated from user's private notifications
      return {
        ...state,
        // currentUserNotificationCount: payload.transactions.length, // Commented out to preserve private data count
      };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case DISMISS_SYSTEM_NOTIFICATION:
      // Only allow dismissal of dismissible system notifications
      const notificationToDismiss = state.currentUserSystemNotifications.find(
        notification => notification.id === payload
      );

      // If notification is not dismissible, don't remove it
      if (
        notificationToDismiss &&
        notificationToDismiss.dismissible === false
      ) {
        return state; // Return unchanged state
      }

      // Remove the dismissible notification
      const updatedSystemNotifications = state.currentUserSystemNotifications.filter(
        notification => notification.id !== payload
      );

      // Recalculate notification count only if something was actually dismissed
      const userNotifications =
        state.currentUser?.attributes?.profile?.privateData?.notifications ||
        [];
      const userUnreadCount = userNotifications.filter(n => !n.read).length;
      const systemUnreadCount = updatedSystemNotifications.filter(n => !n.read)
        .length;

      return {
        ...state,
        currentUserSystemNotifications: updatedSystemNotifications,
        currentUserNotificationCount: userUnreadCount + systemUnreadCount,
      };

    case CLEAR_ALL_USER_NOTIFICATIONS: {
      const updatedUser = {
        ...state.currentUser,
        attributes: {
          ...state.currentUser.attributes,
          profile: {
            ...state.currentUser.attributes.profile,
            privateData: {
              ...state.currentUser.attributes.profile.privateData,
              notifications: [],
            },
          },
        },
      };
      const systemUnread = state.currentUserSystemNotifications.filter(n => !n.read).length;
      return {
        ...state,
        currentUser: updatedUser,
        currentUserNotificationCount: systemUnread,
      };
    }

    case CLEAR_LISTING_PRICING_NOTIFICATIONS: {
      const existingNotifications =
        state.currentUser?.attributes?.profile?.privateData?.notifications || [];
      const updatedNotifications = existingNotifications.filter(
        n => !(n.link && n.link.includes(payload) && n.link.includes('/pricing'))
      );
      const updatedUser = {
        ...state.currentUser,
        attributes: {
          ...state.currentUser.attributes,
          profile: {
            ...state.currentUser.attributes.profile,
            privateData: {
              ...state.currentUser.attributes.profile.privateData,
              notifications: updatedNotifications,
            },
          },
        },
      };
      const systemUnread = state.currentUserSystemNotifications.filter(n => !n.read).length;
      const userUnread = updatedNotifications.filter(n => !n.read).length;
      return {
        ...state,
        currentUser: updatedUser,
        currentUserNotificationCount: userUnread + systemUnread,
      };
    }

    case REMOVE_USER_NOTIFICATION: {
      const existingNotifications =
        state.currentUser?.attributes?.profile?.privateData?.notifications || [];
      const updatedNotifications = existingNotifications.filter(
        n => n.id !== payload
      );
      const updatedUser = {
        ...state.currentUser,
        attributes: {
          ...state.currentUser.attributes,
          profile: {
            ...state.currentUser.attributes.profile,
            privateData: {
              ...state.currentUser.attributes.profile.privateData,
              notifications: updatedNotifications,
            },
          },
        },
      };
      const systemUnread = state.currentUserSystemNotifications.filter(n => !n.read).length;
      const userUnread = updatedNotifications.filter(n => !n.read).length;
      return {
        ...state,
        currentUser: updatedUser,
        currentUserNotificationCount: userUnread + systemUnread,
      };
    }

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return !!(
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

// Selector to get system notifications with listings check
export const getCurrentUserSystemNotifications = state => {
  const { user } = state;
  const {
    currentUser,
    currentUserHasListings,
    currentUserSystemNotifications,
  } = user;

  // Check ManageListingsPage state as well for more accurate listings data
  const manageListingsPageResults =
    state.ManageListingsPage?.currentPageResultIds;
  const hasListingsFromManagePage =
    manageListingsPageResults && manageListingsPageResults.length > 0;

  if (!currentUser || !currentUserSystemNotifications) {
    return [];
  }

  // Use ManageListingsPage state if available, fallback to user state
  const hasListings =
    hasListingsFromManagePage !== undefined
      ? hasListingsFromManagePage
      : currentUserHasListings;

  // Filter out notifications that require listings when user has no listings
  return currentUserSystemNotifications.filter(notification => {
    if (notification.requiresListings) {
      return hasListings;
    }
    return true;
  });
};

// Selector to get total notification count including system notifications
export const getCurrentUserTotalNotificationCount = state => {
  const { user } = state;
  const userNotifications =
    user.currentUser?.attributes?.profile?.privateData?.notifications || [];
  const systemNotifications = getCurrentUserSystemNotifications(state);

  const userUnreadCount = userNotifications.filter(n => !n.read).length;
  const systemUnreadCount = systemNotifications.filter(n => !n.read).length;
  const totalCount = userUnreadCount + systemUnreadCount;

  return totalCount;
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({
  type: CURRENT_USER_SHOW_REQUEST,
});

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (hasListings, listingsCount) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings, listingsCount },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = error => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: error,
});

export const updateSavedSearchesCount = count => ({
  type: UPDATE_SAVED_SEARCHES_COUNT,
  payload: { count },
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const listingsCount = response.data.meta?.totalItems || 0;
      const hasPublishedListings = listingsCount > 0;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings, listingsCount));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (
  dispatch,
  getState,
  sdk
) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve({});
  }
  // Set in-progress, no errors
  dispatch(currentUserShowRequest());

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error(
          'Expected a resource in the sdk.currentUser.show response'
        );
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(
      new Error('Verification email sending already in progress')
    );
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const dismissSystemNotification = notificationId => ({
  type: DISMISS_SYSTEM_NOTIFICATION,
  payload: notificationId,
});

export const clearAllUserNotifications = () => (dispatch, getState, sdk) => {
  dispatch({ type: CLEAR_ALL_USER_NOTIFICATIONS });

  const { currentUser } = getState().user;
  return sdk.currentUser
    .updateProfile({
      privateData: {
        ...currentUser.attributes.profile.privateData,
        notifications: [],
      },
    })
    .catch(() => {});
};

export const clearListingPricingNotifications = listingId => (dispatch, getState, sdk) => {
  dispatch({ type: CLEAR_LISTING_PRICING_NOTIFICATIONS, payload: listingId });

  const { currentUser } = getState().user;
  const notifications =
    currentUser?.attributes?.profile?.privateData?.notifications || [];
  const updatedNotifications = notifications.filter(
    n => !(n.link && n.link.includes(listingId) && n.link.includes('/pricing'))
  );

  return sdk.currentUser
    .updateProfile({
      privateData: {
        ...currentUser.attributes.profile.privateData,
        notifications: updatedNotifications,
      },
    })
    .catch(() => {});
};

export const removeUserNotification = notificationId => (dispatch, getState, sdk) => {
  // Optimistic update — notification disappears immediately
  dispatch({ type: REMOVE_USER_NOTIFICATION, payload: notificationId });

  const { currentUser } = getState().user;
  const notifications =
    currentUser?.attributes?.profile?.privateData?.notifications || [];
  const updatedNotifications = notifications.filter(n => n.id !== notificationId);

  return sdk.currentUser
    .updateProfile({
      privateData: {
        ...currentUser.attributes.profile.privateData,
        notifications: updatedNotifications,
      },
    })
    .catch(() => {
      // Silently ignore — the optimistic state is already correct
    });
};
